import React, { useEffect, useState } from 'react'
import { message } from 'antd'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import SaveIcon from '@mui/icons-material/Save'
import ClearIcon from '@mui/icons-material/Clear'

import useAxios, { axiosInstance } from '../../../../../../../hooks/useAxios'
import { EDistributionChannelType } from '../../../../../../../types/distribution-channel'
import CircularLoading from '../../../../../../../components/CircularProgress'
import { IAddress, IAddressThailand } from '../../../../../../../types/address'
import { ILeadCustomer } from '../../../../../../../types/lead'
import SelectSearch, {
  IOptionSelect,
} from '../../../../../../../components/SelectSearch'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../../constants/errors'

interface IAddressFormModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  address?: ILeadCustomer
  objectId?: string
  onUpdateAddressListSuccess: () => void
}

const getLeadAddressFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string().required('กรุณากรอกชื่อลูกค้า'),
    // email: Yup.string().nullable().email('รูปแบบอีเมลไม่ถูกต้อง'),
    facebook: Yup.string(),
    phone: Yup.string().required('กรุณากรอกเบอร์โทรศัพท์'),
    address: Yup.string().required('กรุณากรอกที่อยู่'),
    subDistrict: Yup.string().required('กรุณากรอกแขวง/ตำบล'),
    district: Yup.string().required('กรุณากรอกเขต/อำเภอ'),
    province: Yup.string().required('กรุณากรอกจังหวัด'),
    zipcode: Yup.string().required('กรุณากรอกรหัสไปรษณีย์'),
  })
}

export const LeadEditAddressModal = (props: IAddressFormModalProps) => {
  const { isOpen, onClose, address, onUpdateAddressListSuccess, objectId } =
    props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [searchAddress, setSearchAddress] = useState<string>('')
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate()
  }

  useEffect(() => {
    setIsLoading(true)
    formik.setValues({
      ...address!,
    })
    setIsLoading(false)
  }, [isOpen, address])

  const formik = useFormik<ILeadCustomer>({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      address: '',
      facebook: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
    } as ILeadCustomer,
    validationSchema: getLeadAddressFormValidationSchema(),
    onSubmit: async (values: ILeadCustomer & { lead?: any }) => {
      setIsLoading(true)

      const phone = values.phone
      if (!phone.startsWith('0')) {
        message.error('เบอร์โทรศัพท์ควรขึ้นต้นด้วย 0')
        setIsLoading(false)
        return
      }
      //check phone number format
      const phoneRegex = /^0\d{8,9}$/
      if (!phoneRegex.test(phone)) {
        message.error(
          'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง (ต้องมี 9-10 หลัก) และเฉพาะตัวเลขเท่านั้น',
        )
        setIsLoading(false)
        return
      }

      const { lead, ...rest } = values

      try {
        await axiosInstance.put(`/lead/change-address/${objectId}`, rest)
        message.success(`แก้ไขที่อยู่ ${values?.address} สำเร็จ`)
        formik.resetForm()
        onClose(false)
        onUpdateAddressListSuccess()
        setResetAutoComplete(new Date().getTime().toString())
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    },
  })

  return (
    <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="md">
      <CircularLoading isLoading={isLoading} />
      <DialogTitle> แก้ไขที่อยู่ </DialogTitle>
      <DialogContent>
        <Box component="form" mt={1} onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} lg={12}>
              <TextField
                id="name"
                fullWidth
                label="ชื่อลูกค้า"
                name="name"
                helperText={
                  formik.touched.name && (formik.errors.name as string)
                }
                error={!!(formik.touched.name && formik.errors.name)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="email"
                fullWidth
                label="อีเมล"
                name="email"
                helperText={
                  formik.touched.email && (formik.errors.email as string)
                }
                error={!!(formik.touched.email && formik.errors.email)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.email}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="facebook"
                fullWidth
                label="Facebook"
                name="facebook"
                helperText={
                  formik.touched.facebook && (formik.errors.facebook as string)
                }
                error={!!(formik.touched.facebook && formik.errors.facebook)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.facebook}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="address"
                fullWidth
                label="ที่อยู่"
                name="address"
                helperText={
                  formik.touched.address && (formik.errors.address as string)
                }
                error={!!(formik.touched.address && formik.errors.address)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.address}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="phone"
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phone"
                helperText={
                  formik.touched.phone && (formik.errors.phone as string)
                }
                error={!!(formik.touched.phone && formik.errors.phone)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.phone}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <SelectSearch<IAddressThailand>
                resetAutoComplete={resetAutoComplete}
                label={'ค้นหาที่อยู่'}
                onSelect={(e) => {
                  formik.setFieldValue('subDistrict', e?.value.subDistrict)
                  formik.setFieldValue('district', e?.value.district)
                  formik.setFieldValue('province', e?.value.province)
                  formik.setFieldValue('zipcode', e?.value.zipcode)
                }}
                search={searchAddress}
                onSearch={handleSearchAddress}
                options={
                  addressList.data?.map((address: IAddressThailand) => ({
                    label: `${address.subDistrict} ${address.district} ${address.province} ${address.zipcode}`,
                    value: address,
                  })) || ([] as IOptionSelect<IAddressThailand>[])
                }
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="address"
                fullWidth
                label="ที่อยู่"
                name="address"
                helperText={
                  formik.touched.address && (formik.errors.address as string)
                }
                error={!!(formik.touched.address && formik.errors.address)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.address}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="subDistrict"
                fullWidth
                label="แขวง/ตำบล"
                name="subDistrict"
                helperText={
                  formik.touched.subDistrict &&
                  (formik.errors.subDistrict as string)
                }
                error={
                  !!(formik.touched.subDistrict && formik.errors.subDistrict)
                }
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.subDistrict}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="district"
                fullWidth
                label="อำเภอ"
                name="district"
                helperText={
                  formik.touched.district && (formik.errors.district as string)
                }
                error={!!(formik.touched.district && formik.errors.district)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.district}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="province"
                fullWidth
                label="จังหวัด"
                name="province"
                helperText={
                  formik.touched.province && (formik.errors.province as string)
                }
                error={!!(formik.touched.province && formik.errors.province)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.province}
              />
            </Grid>
            <Grid item xs={12} lg={12}>
              <TextField
                id="zipcode"
                fullWidth
                label="รหัสไปรษณีย์"
                name="zipcode"
                helperText={
                  formik.touched.zipcode && (formik.errors.zipcode as string)
                }
                error={!!(formik.touched.zipcode && formik.errors.zipcode)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.zipcode}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} mt={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                startIcon={<SaveIcon />}
                color="primary"
                type="submit"
                size="large"
                variant="contained"
              >
                {address ? 'แก้ไขที่อยู่' : 'เพิ่มที่อยู่'}
              </Button>
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                size="large"
                variant="contained"
                onClick={() => onClose(false)}
                startIcon={<ClearIcon />}
              >
                ปิดหน้าต่าง
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
